import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Pencil, Plus, Radar, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Spinner } from "@/components/ui/spinner";
import { Badge } from "@/components/ui/badge";
import { DataTable } from "@/components/data-table";
import { EmptyState } from "@/components/ui/empty";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    createAdSource,
    deleteAdSource,
    listAdSourcesPaged,
    updateAdSource,
    type CreateAdSourceRequest,
} from "@/api/ad-sources";
import { Pagination } from "@/components/pagination";
import { apiErrorMessage } from "@/lib/api";
import { formatRelativeTime } from "@/lib/utils";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { RowActions } from "@/components/ui/row-actions";
import type { AdSourceDto } from "@/types/api";
import { AffiliateSelect } from "@/components/affiliate-select";
import { AffiliateCell, useAffiliateLookup } from "@/components/advertiser-cell";
import { ownerFilterFromUrl, ownerFilterParam, ownerFilterToUrl } from "@/api/commerce";

interface FormState {
    slug: string;
    label: string;
    clickIdParams: string;
    enabled: boolean;
    notes: string;
}

const EMPTY: FormState = { slug: "", label: "", clickIdParams: "", enabled: true, notes: "" };

function fromDto(dto: AdSourceDto): FormState {
    return {
        slug: dto.slug,
        label: dto.label,
        clickIdParams: dto.clickIdParams.join(", "),
        enabled: dto.enabled,
        notes: dto.notes ?? "",
    };
}

function toCreateRequest(form: FormState): CreateAdSourceRequest {
    const params = form.clickIdParams
        .split(",")
        .map((p) => p.trim())
        .filter(Boolean);
    return {
        slug: form.slug.trim(),
        label: form.label.trim(),
        clickIdParams: params,
        enabled: form.enabled,
        notes: form.notes.trim() || null,
    };
}

export function AdSourcesPage(): React.ReactNode {
    const queryClient = useQueryClient();
    const confirm = useConfirm();
    const [editing, setEditing] = React.useState<AdSourceDto | null>(null);
    const [creating, setCreating] = React.useState(false);

    const PAGE_SIZE = 20;
    const [pageIndex, setPageIndex] = React.useState(0);

    // Affiliate filter — URL-persisted, follows the same pattern as the rest
    // of the list pages. undefined = "All", null = "Unassigned" (-1 wire).
    const [searchParams, setSearchParams] = useSearchParams();
    const affiliateId = ownerFilterFromUrl(searchParams.get("affiliateId"));
    const setAffiliateId = React.useCallback(
        (next: number | null | undefined): void => {
            setSearchParams(
                (p) => {
                    const v = ownerFilterToUrl(next);
                    if (v == null) p.delete("affiliateId");
                    else p.set("affiliateId", v);
                    return p;
                },
                { replace: true },
            );
            setPageIndex(0);
        },
        [setSearchParams],
    );
    const affLookup = useAffiliateLookup();
    const showAffiliateColumn = affLookup.affiliates.length > 0;

    const listQuery = useQuery({
        queryKey: ["ad-sources", pageIndex, affiliateId],
        queryFn: () => listAdSourcesPaged({
            page: pageIndex,
            size: PAGE_SIZE,
            sort: "label,asc",
            affiliateId: ownerFilterParam(affiliateId),
        }),
        placeholderData: (prev) => prev,
    });

    const createMut = useMutation({
        mutationFn: (request: CreateAdSourceRequest) => createAdSource(request),
        onSuccess: () => {
            toast.success("Ad source created");
            queryClient.invalidateQueries({ queryKey: ["ad-sources"] });
            setCreating(false);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Create failed")),
    });

    const updateMut = useMutation({
        mutationFn: ({ id, request }: { id: number; request: CreateAdSourceRequest }) =>
            updateAdSource(id, {
                label: request.label,
                clickIdParams: request.clickIdParams,
                enabled: request.enabled,
                notes: request.notes,
            }),
        onSuccess: () => {
            toast.success("Ad source updated");
            queryClient.invalidateQueries({ queryKey: ["ad-sources"] });
            setEditing(null);
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Update failed")),
    });

    const deleteMut = useMutation({
        mutationFn: (id: number) => deleteAdSource(id),
        onSuccess: () => {
            toast.success("Ad source deleted");
            queryClient.invalidateQueries({ queryKey: ["ad-sources"] });
        },
        onError: (err) => toast.error(apiErrorMessage(err, "Delete failed")),
    });

    return (
        <>
            <PageHeader
                title="Traffic networks"
                description="The ad platforms you buy traffic from — Facebook, Google, AdMaven, TikTok, etc. Each network passes a click-ID parameter the platform uses to attribute visits and leads back to a specific campaign."
                actions={
                    <Button data-testid="ad-sources-new" onClick={() => setCreating(true)}>
                        <Plus className="h-4 w-4" />
                        New network
                    </Button>
                }
            />
            <div className="p-6 space-y-3">
                {/* What-is-this callout — new operators have no idea why this page exists. */}
                <div className="rounded-md border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                    <p>
                        <strong className="text-foreground">When to add a network:</strong> any time you start
                        sending traffic from a new ad platform. The platform captures the configured click-ID
                        params (e.g. <code className="font-mono text-xs">fbclid</code>,
                        {" "}<code className="font-mono text-xs">gclid</code>) on every visit and attaches them
                        to the lead — your manifests then read them automatically.
                    </p>
                </div>
                {showAffiliateColumn && (
                    <div className="flex items-center gap-2">
                        <div className="w-[220px]">
                            <AffiliateSelect
                                value={affiliateId}
                                onChange={setAffiliateId}
                                data-testid="ad-sources-affiliate-filter"
                            />
                        </div>
                    </div>
                )}
                <Card>
                    <CardContent className="p-0">
                        {listQuery.isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <Spinner size="lg" />
                            </div>
                        ) : listQuery.data && listQuery.data.content.length > 0 ? (
                            <DataTable<AdSourceDto>
                                dataTestid="ad-sources-table"
                                className="rounded-none border-0"
                                columns={[
                                    {
                                        key: "slug",
                                        header: "Slug",
                                        cell: (s) => (
                                            <code className="font-mono text-xs">{s.slug}</code>
                                        ),
                                    },
                                    {
                                        key: "label",
                                        header: "Label",
                                        cell: (s) => <span className="font-medium">{s.label}</span>,
                                    },
                                    {
                                        key: "params",
                                        header: "Click-ID params",
                                        cell: (s) => (
                                            <span className="font-mono text-xs text-muted-foreground">
                                                {s.clickIdParams.join(", ")}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: "enabled",
                                        header: "Status",
                                        cell: (s) => (
                                            <Badge variant={s.enabled ? "success" : "muted"}>
                                                {s.enabled ? "enabled" : "disabled"}
                                            </Badge>
                                        ),
                                    },
                                    ...(showAffiliateColumn ? [{
                                        key: "affiliate",
                                        header: "Affiliate",
                                        cell: (s: AdSourceDto) => (
                                            <AffiliateCell affiliateId={s.affiliateId} lookup={affLookup.byId} />
                                        ),
                                    }] : []),
                                    {
                                        key: "updated",
                                        header: "Updated",
                                        cell: (s) => (
                                            <span className="text-xs text-muted-foreground">
                                                {formatRelativeTime(s.updatedAt)}
                                            </span>
                                        ),
                                    },
                                    {
                                        key: "actions",
                                        header: "",
                                        cell: (s) => (
                                            <RowActions>
                                                <Button
                                                    data-testid={`ad-source-edit-${s.slug}`}
                                                    aria-label={`Edit ${s.label}`}
                                                    title="Edit"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditing(s);
                                                    }}
                                                >
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                                <Button
                                                    data-testid={`ad-source-delete-${s.slug}`}
                                                    aria-label={`Delete ${s.label}`}
                                                    title="Delete"
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        confirm.show({
                                                            title: `Delete "${s.label}"?`,
                                                            description: `Removes the "${s.slug}" network from attribution. Manifests referencing this slug will fail validation until you re-add it.`,
                                                            destructive: true,
                                                            confirmLabel: "Delete network",
                                                            onConfirm: async () => {
                                                                await new Promise<void>((resolve, reject) => {
                                                                    deleteMut.mutate(s.id, { onSuccess: () => resolve(), onError: (e) => reject(e) });
                                                                });
                                                            },
                                                        });
                                                    }}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </Button>
                                            </RowActions>
                                        ),
                                    },
                                ]}
                                rows={listQuery.data.content}
                                rowKey={(s) => s.id}
                            />
                        ) : (
                            <EmptyState
                                icon={<Radar className="h-8 w-8" />}
                                title="No traffic networks yet"
                                description="Add the networks you buy traffic from so leads can be attributed to the right source."
                                action={<Button onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> Add network</Button>}
                            />
                        )}
                    </CardContent>
                </Card>
                {listQuery.data && (
                    <Pagination
                        page={listQuery.data}
                        pageSize={PAGE_SIZE}
                        currentPage={pageIndex}
                        onPage={setPageIndex}
                    />
                )}
            </div>

            <AdSourceForm
                open={creating}
                initial={EMPTY}
                title="New ad source"
                submitLabel="Create"
                slugEditable={true}
                onClose={() => setCreating(false)}
                submitting={createMut.isPending}
                onSubmit={(form) => createMut.mutate(toCreateRequest(form))}
            />
            <AdSourceForm
                open={editing != null}
                initial={editing ? fromDto(editing) : EMPTY}
                title={editing ? `Edit ${editing.label}` : ""}
                submitLabel="Save"
                slugEditable={false}
                onClose={() => setEditing(null)}
                submitting={updateMut.isPending}
                onSubmit={(form) => {
                    if (!editing) return;
                    updateMut.mutate({ id: editing.id, request: toCreateRequest(form) });
                }}
            />
            {confirm.dialog}
        </>
    );
}

interface AdSourceFormProps {
    open: boolean;
    initial: FormState;
    title: string;
    submitLabel: string;
    slugEditable: boolean;
    onClose: () => void;
    submitting: boolean;
    onSubmit: (form: FormState) => void;
}

function AdSourceForm({
    open,
    initial,
    title,
    submitLabel,
    slugEditable,
    onClose,
    submitting,
    onSubmit,
}: AdSourceFormProps): React.ReactNode {
    const [form, setForm] = React.useState<FormState>(initial);
    // Reset only when the dialog OPENS — not on every parent re-render. `initial`
    // is a freshly-allocated object each render, so keying the reset on it would
    // wipe the operator's in-progress edits whenever a background/refocus refetch
    // re-renders the parent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    React.useEffect(() => {
        if (open) setForm(initial);
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        Click-ID params are URL search-param names the network appends to outbound
                        clicks (e.g. <code>fbclid</code>, <code>gclid</code>).
                    </DialogDescription>
                </DialogHeader>
                <form
                    data-testid="ad-source-form"
                    className="space-y-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        onSubmit(form);
                    }}
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="ad-source-slug">Slug</Label>
                        <Input
                            id="ad-source-slug"
                            data-testid="ad-source-slug"
                            value={form.slug}
                            onChange={(e) => setForm({ ...form, slug: e.target.value })}
                            disabled={!slugEditable}
                            placeholder="facebook"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="ad-source-label">Label</Label>
                        <Input
                            id="ad-source-label"
                            data-testid="ad-source-label"
                            value={form.label}
                            onChange={(e) => setForm({ ...form, label: e.target.value })}
                            placeholder="Facebook / Meta Ads"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="ad-source-params">Click-ID params (comma-separated)</Label>
                        <Input
                            id="ad-source-params"
                            data-testid="ad-source-params"
                            value={form.clickIdParams}
                            onChange={(e) => setForm({ ...form, clickIdParams: e.target.value })}
                            placeholder="fbclid, fb_click_id"
                            required
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="ad-source-notes">Notes (optional)</Label>
                        <Textarea
                            id="ad-source-notes"
                            data-testid="ad-source-notes"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            rows={2}
                        />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            data-testid="ad-source-enabled"
                            checked={form.enabled}
                            onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                        />
                        Enabled (new pages pre-select this source)
                    </label>
                    <DialogFooter>
                        <Button type="button" variant="outline" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={submitting} data-testid="ad-source-submit">
                            {submitting ? "Saving…" : submitLabel}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
